import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Upload } from "lucide-react";
import { useApi, errMsg, downloadBlob } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FIELD_LABELS = { name: "Operator", certificate: "Certificate", phone: "Phone", email: "Email", address: "Address", city: "City", state: "State",
  zip: "Zip", country: "Country", tail_number: "Tail", aircraft_type: "Aircraft type", aircraft_category: "Category", year: "YOM", year_refurbished: "YOR", seats: "Seats" };
const STATUS_CLS = { new: "text-emerald-400", updated: "text-amber-300", unchanged: "text-zinc-500" };

function Summary({ s }) {
  const items = [["New operators", s.new_operators], ["Updated operators", s.updated_operators], ["Unchanged", s.unchanged_operators],
    ["New tails", s.new_tails], ["Updated tails", s.updated_tails]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="import-summary">
      {items.map(([l, v]) => (
        <div key={l} className="border border-white/10 p-3">
          <p className="font-mono-data text-xl text-white" data-testid={`import-summary-${l.toLowerCase().replace(/\s+/g, "-")}`}>{v}</p>
          <p className="text-[11px] text-zinc-500 uppercase tracking-wider">{l}</p>
        </div>
      ))}
    </div>
  );
}

function Preview({ r }) {
  return (
    <div className="grid gap-4">
      <Summary s={r.summary} />
      <div className="text-xs text-zinc-400" data-testid="import-columns">
        <span className="text-zinc-500">Columns matched: </span>
        {Object.entries(r.columns).map(([f, h]) => `${h} → ${FIELD_LABELS[f] || f}`).join(" · ")}
        {r.ignored.length > 0 && <p className="mt-1 text-zinc-500">Not used: {r.ignored.join(", ")}</p>}
      </div>
      {r.warnings.length > 0 && (
        <ul className="text-xs text-amber-300/90 max-h-24 overflow-auto" data-testid="import-warnings">{r.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
      )}
      <ul className="max-h-64 overflow-auto border-t border-white/10" data-testid="import-preview-list">
        {r.operators.map((o) => (
          <li key={o.name} className="flex flex-wrap gap-x-3 py-2 border-b border-white/[0.06] text-sm">
            <span className="text-white flex-1 min-w-[10rem]">{o.name}</span>
            <span className={`text-xs uppercase tracking-wider ${STATUS_CLS[o.status]}`}>{o.status}</span>
            <span className="text-xs text-zinc-500 w-full">
              {[o.changed.length && `Updates ${o.changed.join(", ")}`, o.new_tails.length && `Adds tails ${o.new_tails.join(", ")}`,
                o.updated_tails.length && `Updates tails ${o.updated_tails.join(", ")}`].filter(Boolean).join(" · ")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function OperatorImport({ onClose, onDone }) {
  const api = useApi();
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (f, dry) => {
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const r = await api.post(`/operators/import?dry_run=${dry}`, fd);
      return r.data;
    } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(false); }
  };
  const pick = async (f) => {
    if (!f) return;
    setFile(f);
    setRes(await send(f, true));
  };
  const apply = async () => {
    const r = await send(file, false);
    if (!r) return;
    toast.success(`Imported: ${r.summary.new_operators} new, ${r.summary.updated_operators} updated operators · ${r.summary.new_tails} new tails`);
    onDone();
    onClose();
  };
  const changes = res && res.summary.new_operators + res.summary.updated_operators;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#111319] border-white/10 max-w-2xl" data-testid="import-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-2xl font-normal text-white">Import operators from Excel</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400">One row per operator. Put several tails in one cell separated by commas. Operators already listed are updated and new tails are added; blank cells never erase saved details.</p>
        <div className="flex flex-wrap gap-3">
          <input ref={input} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid="import-file-input" />
          <button className="frame-btn gold" onClick={() => input.current.click()} disabled={busy} data-testid="import-choose-file-btn"><Upload size={13} /> {file ? "Choose another file" : "Choose file"}</button>
          <button className="frame-btn" onClick={() => downloadBlob(api, "/operators/import/template", "JSG-Operators-Template.xlsx").catch((e) => toast.error(errMsg(e)))} data-testid="import-template-btn"><Download size={13} /> Template</button>
        </div>
        {file && <p className="text-xs text-zinc-400 flex items-center gap-2" data-testid="import-file-name"><FileSpreadsheet size={13} /> {file.name}{busy && " · reading…"}</p>}
        {res && <Preview r={res} />}
        <div className="flex justify-end gap-3 pt-2">
          <button className="frame-btn" onClick={onClose} data-testid="import-cancel-btn">Cancel</button>
          <button className="frame-btn gold" disabled={busy || !changes} onClick={apply} data-testid="import-confirm-btn">
            {busy && res ? "Importing…" : res && !changes ? "Nothing to import" : `Import${changes ? ` ${changes} operator${changes > 1 ? "s" : ""}` : ""}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
